import type {
    ReactiveFormChangedField,
    ReactiveFormValue
} from '@fitogest/reactive-form-builder/interfaces';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';

export type ParcelasStableFormClient = {
    readonly nombre?: string | null;
    readonly apellidos?: string | null;
};

export type ParcelasStableFormChangeContext = {
    readonly formModel: ReactiveFormValue<ParcelasModel>;
    readonly changedField: ReactiveFormChangedField<ParcelasModel>;
    readonly env: ParcelasEnvironmentDefinition;
    readonly client: ParcelasStableFormClient | null | undefined;
    readonly resolveDropdownRawValue: <TEntity>(fieldName: string, value: unknown) => TEntity | null;
};

export type ParcelasStableFormChangeResult = {
    readonly formModel: ReactiveFormValue<ParcelasModel>;
    readonly tag: ReactiveFormChangedField<ParcelasModel>;
    readonly markNombreTouched: boolean;
};

/**
 * Resolves business reactions to stable Reactive Form Builder changes.
 *
 * The component applies the returned model and UI flags, while this adapter owns the field-specific rules that
 * derive parcel names and keep stable change handling testable outside Angular lifecycle code.
 */
export class ParcelasStableFormChangeAdapter {

    public static resolveChange(context: ParcelasStableFormChangeContext): ParcelasStableFormChangeResult {
        return {
            formModel: ParcelasStableFormChangeAdapter.resolveModel(context),
            tag: context.changedField,
            markNombreTouched: context.changedField === 'nombre',
        };
    }

    private static resolveModel(context: ParcelasStableFormChangeContext): ReactiveFormValue<ParcelasModel> {
        if (context.changedField !== 'id_variedad' || context.env.appName !== 'avafitogest') {
            return context.formModel;
        }

        const selectedVariedad = context.resolveDropdownRawValue<VisitasVariedadMesModel>(
            'id_variedad',
            context.formModel.id_variedad,
        );
        const variedadName = selectedVariedad?.variedad ?? '';

        if (variedadName === '') {
            return context.formModel;
        }

        return {
            ...context.formModel,
            nombre: ParcelasStableFormChangeAdapter.buildAvaFitogestParcelName(context.client, variedadName),
        };
    }

    private static buildAvaFitogestParcelName(
        client: ParcelasStableFormClient | null | undefined,
        variedadName: string
    ): string {
        return (client?.nombre ?? '') + '_' + (client?.apellidos ?? '') + '_' + variedadName;
    }
}
